import { useRef, useState, type ReactNode } from 'react';
import { notesService } from '@/core/services/notes';
import { notifyError } from '@/core/notify/notify';
import type { NoteDialogData, NoteRow } from '@/core/models/notes.models';
import { validateForm } from '@/features/manage/formRules';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import { DialogClose, DialogContent, type DialogRef } from '@/kit/dialog';
import './NotesDialogs.scss';

/**
 * The frame every additional-notes dialog shares: heading with the X, the body, and Save / Cancel.
 * `headerClassName` is the heading's class (`sidenav-header` or `sidenav-header border-none mb-0`), `rootClassName`
 * the component-scoped SCSS root of the Angular dialog.
 */
export function NoteDialogShell({
  title,
  headerClassName = 'sidenav-header',
  rootClassName = '',
  isSaving,
  onSave,
  children,
}: {
  title: ReactNode;
  headerClassName?: string;
  rootClassName?: string;
  isSaving: boolean;
  onSave: () => void;
  children: ReactNode;
}) {
  return (
    <div className={`mat-dialog-wrapper notes-dialog ${rootClassName}`.trim()}>
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className={headerClassName}>
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>{title}</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="notes-dialog-body">{children}</div>
          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" busy={isSaving} onClick={onSave}>
              Save
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}

/**
 * A guidance dialog: heading, wording, Ok and Cancel (both just close it). Opened without data.
 */
export function InfoDialogShell({ title, paragraphs }: { title: string; paragraphs: string[] }) {
  return (
    <div className="mat-dialog-wrapper notes-dialog">
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>{title}</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="w-510">
            {paragraphs.map((text, index) => (
              <p key={index}>{text}</p>
            ))}
          </div>
          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <DialogClose as="button" type="button" className="action-button primary-button">
              Ok
            </DialogClose>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}

/** The message the API gives for a missing mandatory value, shown under its field once a save was tried. */
export function FieldMessage({ show, children }: { show: boolean; children: ReactNode }) {
  return show ? (
    <p className="text-red font-size-12 mb-0" role="alert">
      {children}
    </p>
  ) : null;
}

/**
 * The dialogs' save: `NotesService.saveFromDialog`, closing with the saved row; an error toast keeps the dialog
 * open. With no data (never the case in the app) Save simply closes. Pass `isValid` to stop a save whose mandatory
 * values are missing before it reaches the API: the shared mandatory-fields toast shows and `submitted` turns true
 * so the dialog can draw its red outlines and messages. A second click while a save runs is ignored.
 */
export function useNoteSave(data: NoteDialogData | null, dialogRef: DialogRef<NoteRow>) {
  const [isSaving, setIsSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const inFlight = useRef(false);

  const save = (buildContent: () => unknown, isValid = true) => {
    if (!data) {
      dialogRef.close();
      return;
    }
    if (inFlight.current) {
      return;
    }
    setSubmitted(true);
    if (!validateForm(isValid)) {
      return;
    }
    const content = buildContent();
    inFlight.current = true;
    setIsSaving(true);
    notesService
      .saveFromDialog(data, content)
      .then(
        (row) => dialogRef.close(row),
        (error) => notifyError(error),
      )
      .finally(() => {
        inFlight.current = false;
        setIsSaving(false);
      });
  };

  return { isSaving, save, submitted };
}
